/**
 * @module useBreakpoints
 *
 * @see https://0.vuetifyjs.com/composables/plugins/use-breakpoints
 *
 * @remarks
 * Responsive breakpoint detection composable with window resize handling.
 *
 * Key features:
 * - matchMedia-based detection for zoom-accurate breakpoints
 * - Six built-in breakpoints (xs, sm, md, lg, xl, xxl) with Vuetify 4 default widths
 * - `isMobile` flag and configurable `mobileBreakpoint`
 * - Automatic resize listener with cleanup
 * - SSR-safe with hydration flush
 * - Custom breakpoint configuration
 *
 * Perfect for responsive layouts and conditional rendering based on screen size.
 *
 * @example
 * ```ts
 * import { useBreakpoints } from '@vuetify/v0'
 *
 * const { current, isMobile, smAndUp } = useBreakpoints()
 * console.log(current.value) // 'md'
 * ```
 */

// Composables
import { createPluginContext } from '#v0/composables/createPlugin'
import { useWindowEventListener } from '#v0/composables/useEventListener'

// Globals
import { IN_BROWSER, SUPPORTS_MATCH_MEDIA } from '#v0/constants/globals'

// Utilities
import { isNumber, mergeDeep } from '#v0/utilities'
import { readonly, shallowRef } from 'vue'

// Types
import type { ShallowRef } from 'vue'

export type BreakpointName = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

const BREAKPOINT_ORDER = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'] as const satisfies readonly BreakpointName[]

export interface BreakpointsContext {
  breakpoints: Readonly<Record<BreakpointName, number>>
  mobileBreakpoint: BreakpointName | number
  name: Readonly<ShallowRef<BreakpointName>>
  width: Readonly<ShallowRef<number>>
  height: Readonly<ShallowRef<number>>
  isMobile: Readonly<ShallowRef<boolean>>
  xs: Readonly<ShallowRef<boolean>>
  sm: Readonly<ShallowRef<boolean>>
  md: Readonly<ShallowRef<boolean>>
  lg: Readonly<ShallowRef<boolean>>
  xl: Readonly<ShallowRef<boolean>>
  xxl: Readonly<ShallowRef<boolean>>
  smAndUp: Readonly<ShallowRef<boolean>>
  mdAndUp: Readonly<ShallowRef<boolean>>
  lgAndUp: Readonly<ShallowRef<boolean>>
  xlAndUp: Readonly<ShallowRef<boolean>>
  smAndDown: Readonly<ShallowRef<boolean>>
  mdAndDown: Readonly<ShallowRef<boolean>>
  lgAndDown: Readonly<ShallowRef<boolean>>
  xlAndDown: Readonly<ShallowRef<boolean>>
  ssr: boolean
  update: () => void
}

export interface BreakpointsOptions {
  namespace?: string
  mobileBreakpoint?: BreakpointName | number
  breakpoints?: Partial<Record<BreakpointName, number>>
  ssr?: { clientWidth: number, clientHeight?: number }
}

export interface BreakpointsPluginOptions extends BreakpointsOptions {}

export interface BreakpointsContextOptions extends BreakpointsOptions {}

/**
 * Creates default breakpoint configuration.
 *
 * @returns The default breakpoint configuration object.
 */
function createDefaultBreakpoints () {
  return {
    mobileBreakpoint: 'lg',
    breakpoints: {
      xs: 0,
      sm: 600,
      md: 840,
      lg: 1145,
      xl: 1545,
      xxl: 2138,
    },
  } as const
}

/**
 * Creates a new breakpoints instance.
 *
 * @param options The options for the breakpoints instance.
 * @returns A new breakpoints instance.
 *
 * @see https://0.vuetifyjs.com/composables/plugins/use-breakpoints
 *
 * @example
 * ```ts
 * import { createBreakpoints } from '@vuetify/v0'
 *
 * const breakpoints = createBreakpoints({
 *   namespace: 'v0:breakpoints',
 *   mobileBreakpoint: 'md',
 *   breakpoints: {
 *     xs: 0,
 *     sm: 600,
 *     md: 840,
 *     lg: 1145,
 *     xl: 1545,
 *     xxl: 2138,
 *   },
 * })
 * ```
 */
export function createBreakpoints (_options: BreakpointsOptions = {}): BreakpointsContext {
  const { ssr, ...options } = _options
  const defaults = createDefaultBreakpoints()
  const { mobileBreakpoint, breakpoints } = mergeDeep(defaults, options as typeof defaults)
  const sorted = Object.entries(breakpoints!).toSorted((a, b) => a[1] - b[1]) as [BreakpointName, number][]
  const names = sorted.map(([n]) => n)
  const mb = isNumber(mobileBreakpoint) ? mobileBreakpoint : breakpoints[mobileBreakpoint] ?? breakpoints.md

  // When SSR is configured, both server and client use the same dimensions
  // so rendered markup matches and avoids hydration mismatch.
  // After hydration, update() replaces these with real window dimensions.
  // Without SSR, browser reads window dimensions immediately.
  const initialWidth = ssr
    ? ssr.clientWidth
    : (IN_BROWSER ? window.innerWidth : 0)
  const initialHeight = ssr
    ? (ssr.clientHeight ?? 0)
    : (IN_BROWSER ? window.innerHeight : 0)

  const name = shallowRef<BreakpointName>('xs')
  const width = shallowRef(initialWidth)
  const height = shallowRef(initialHeight)
  const isMobile = shallowRef(false)
  const xs = shallowRef(false)
  const sm = shallowRef(false)
  const md = shallowRef(false)
  const lg = shallowRef(false)
  const xl = shallowRef(false)
  const xxl = shallowRef(false)
  const smAndUp = shallowRef(false)
  const mdAndUp = shallowRef(false)
  const lgAndUp = shallowRef(false)
  const xlAndUp = shallowRef(false)
  const smAndDown = shallowRef(false)
  const mdAndDown = shallowRef(false)
  const lgAndDown = shallowRef(false)
  const xlAndDown = shallowRef(false)

  // Use matchMedia for breakpoint detection to guarantee
  // alignment with CSS media queries at any zoom level.
  // Falls back to a width comparison when matchMedia is unavailable.
  function resolve (value: number, media: boolean) {
    let current: BreakpointName = 'xs'
    for (let i = sorted.length - 1; i >= 0; i--) {
      const px = sorted[i]![1]
      if (media ? window.matchMedia(`(min-width: ${px}px)`).matches : value >= px) {
        current = sorted[i]![0]
        break
      }
    }

    const index = names.indexOf(current)
    /* v8 ignore next 3 -- alternate arm is defensive: mb always resolves to a number via `?? breakpoints.md` */
    const mobile = isNumber(mb)
      ? (media ? !window.matchMedia(`(min-width: ${mb}px)`).matches : value < mb)
      : index < names.indexOf(mobileBreakpoint as BreakpointName)

    return { current, index, mobile }
  }

  function apply ({ current, mobile }: ReturnType<typeof resolve>) {
    const index = BREAKPOINT_ORDER.indexOf(current)

    name.value = current
    isMobile.value = mobile
    xs.value = current === 'xs'
    sm.value = current === 'sm'
    md.value = current === 'md'
    lg.value = current === 'lg'
    xl.value = current === 'xl'
    xxl.value = current === 'xxl'
    smAndUp.value = index >= 1
    mdAndUp.value = index >= 2
    lgAndUp.value = index >= 3
    xlAndUp.value = index >= 4
    smAndDown.value = index <= 1
    mdAndDown.value = index <= 2
    lgAndDown.value = index <= 3
    xlAndDown.value = index <= 4
  }

  // Initial state resolves through the same matchMedia-based logic as
  // update() so a bare createBreakpoints() matches CSS media queries on
  // first paint. SSR keeps the configured width comparison so server and
  // client markup match; no-matchMedia environments keep the width comparison.
  apply(resolve(initialWidth, !ssr && IN_BROWSER && SUPPORTS_MATCH_MEDIA))

  let listening = false

  function listen () {
    if (listening || !IN_BROWSER) return

    listening = true
    useWindowEventListener('resize', () => {
      update()
    }, { passive: true })
  }

  function update () {
    if (!IN_BROWSER) return

    listen()

    width.value = window.innerWidth
    height.value = window.innerHeight

    apply(resolve(width.value, SUPPORTS_MATCH_MEDIA))
  }

  if (!ssr) listen()

  return {
    breakpoints,
    mobileBreakpoint,
    name: readonly(name),
    width: readonly(width),
    height: readonly(height),
    isMobile: readonly(isMobile),
    xs: readonly(xs),
    sm: readonly(sm),
    md: readonly(md),
    lg: readonly(lg),
    xl: readonly(xl),
    xxl: readonly(xxl),
    smAndUp: readonly(smAndUp),
    mdAndUp: readonly(mdAndUp),
    lgAndUp: readonly(lgAndUp),
    xlAndUp: readonly(xlAndUp),
    smAndDown: readonly(smAndDown),
    mdAndDown: readonly(mdAndDown),
    lgAndDown: readonly(lgAndDown),
    xlAndDown: readonly(xlAndDown),
    ssr: !!ssr,
    update,
  }
}

function createBreakpointsFallback (options: BreakpointsOptions = {}): BreakpointsContext {
  if (options.ssr) return createBreakpoints(options)

  const defaults = createDefaultBreakpoints()

  return {
    breakpoints: defaults.breakpoints,
    mobileBreakpoint: defaults.mobileBreakpoint,
    name: readonly(shallowRef<BreakpointName>('xs')),
    width: readonly(shallowRef(0)),
    height: readonly(shallowRef(0)),
    isMobile: readonly(shallowRef(true)),
    xs: readonly(shallowRef(true)),
    sm: readonly(shallowRef(false)),
    md: readonly(shallowRef(false)),
    lg: readonly(shallowRef(false)),
    xl: readonly(shallowRef(false)),
    xxl: readonly(shallowRef(false)),
    smAndUp: readonly(shallowRef(false)),
    mdAndUp: readonly(shallowRef(false)),
    lgAndUp: readonly(shallowRef(false)),
    xlAndUp: readonly(shallowRef(false)),
    smAndDown: readonly(shallowRef(true)),
    mdAndDown: readonly(shallowRef(true)),
    lgAndDown: readonly(shallowRef(true)),
    xlAndDown: readonly(shallowRef(true)),
    ssr: false,
    update: () => {},
  }
}

export const [createBreakpointsContext, createBreakpointsPlugin, useBreakpoints] =
  /* @__PURE__ */ createPluginContext<BreakpointsContextOptions, BreakpointsContext>(
    'v0:breakpoints',
    options => createBreakpoints(options),
    {
      fallback: () => createBreakpointsFallback(),
      setup: (context, app, options) => {
        if (!options?.ssr) {
          if (IN_BROWSER) context.update()
          return
        }

        // The factory listens on the first update(). This call is that flush,
        // after mount, so the server width survives install.
        const { mount } = app
        app.mount = (...args) => {
          const vm = mount(...args)
          context.update()
          app.mount = mount
          return vm
        }
      },
    },
  )
